'use client'

import { useAtom, useAtomValue, useStore } from 'jotai'
import { PanelRightClose, PanelRightOpen, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Dialog as SheetPrimitive } from 'radix-ui'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { SheetPortal } from '@/components/ui/sheet'
import { OverlayScrollY } from '@/components/widget/overlay-scroll-y'
import { ChatContainerContext } from '@/features/order/chat/lib/chat-container-context'
import OrderFormDetailPanel from '@/features/order/order-form/components/order-form-detail-panel'
import OrderFormSummary from '@/features/order/order-form/components/order-form-summary'
import SavePresetButton from '@/features/order/order-form/components/widget/save-preset-button'
import { OrderFormCollapsibleContext } from '@/features/order/order-form/lib/order-form-collapsible-context'
import { OrderFormProvider } from '@/features/order/order-form/lib/order-form-context'
import {
  ORDER_ROWS_CONFIG_KEY,
  parseOrderRows,
} from '@/features/order/order-form/lib/persisted-order-rows'
import { PopupOrderContext } from '@/features/order/order-form/lib/popup-order-context'
import { useLockPageScroll } from '@/hooks/use-lock-page-scroll'
import { useIsMobileDevice, useIsMobileLayout } from '@/hooks/use-screen'
import { useVisualViewportRect, visualViewportFixedStyle } from '@/hooks/use-visual-viewport'
import { useOrderFormCollapsibleConfig } from '@/service/ui-state.hook'
import {
  orderFormPopupAnchorAtom,
  orderFormPopupCardIdAtom,
  orderFormPopupConfigAtom,
  orderFormPopupExpandedAtom,
  orderFormPopupFormAtom,
  orderFormSelectionAtomFamily,
  type OrderFormServerValues,
  orderFormServerValuesAtomFamily,
  OrderFormSummaryTab,
  orderFormSummaryTabAtomFamily,
  orderFormSummaryTabPickedAtomFamily,
} from '@/state/order'

export const ORDER_FORM_SHEET_HEIGHT = 580
export const ORDER_FORM_SHEET_WIDTH = 420
export const ORDER_FORM_SHEET_EXPANDED_WIDTH = 720
export const ORDER_FORM_SHEET_WIDTH_TRANSITION = { duration: 0.22, ease: 'easeInOut' as const }
export const ORDER_FORM_SHEET_ATTR = 'data-order-form-sheet'

const noop = () => {}

// Cards already re-seeded from the popup config mirror this session. After the first
// open the in-memory atoms are the source of truth — the mirror only matters across
// reloads, so never apply it twice (it would clobber in-session tab changes).
const seededPopupCards = new Set<string>()

function OrderFormSheetErrorState({ onClose }: { onClose: () => void }) {
  const t = useTranslations('orderForm')
  return (
    <div className="bg-foreground/5 flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
      <p className="text-xs text-white/30">{t('orderFormFailedToLoad')}</p>
      <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-white/60">
        {t('close')}
      </Button>
    </div>
  )
}

// Mounted once at the dashboard root (not through WidgetContent), so unlike a normal
// order-form WIDGET — always wrapped in WidgetErrorBoundary, see widget-content.tsx —
// nothing here stopped a render error (e.g. the account/denomination effect loops fixed
// elsewhere in this file's history) from taking down the entire page instead of just this
// panel. Mirrors WidgetErrorBoundary's shape; onClose lets the fallback actually recover
// (clear cardId) instead of leaving a dead panel pinned open.
class OrderFormSheetErrorBoundary extends React.Component<
  { children: React.ReactNode; onClose: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onClose: () => void }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: { onClose: () => void }) {
    // A new cardId (the user closed and reopened the sheet) is a fresh mount attempt —
    // clear a stale error so the next open isn't permanently stuck on the fallback.
    if (this.state.hasError && prevProps.onClose !== this.props.onClose) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      return <OrderFormSheetErrorState onClose={this.props.onClose} />
    }
    return this.props.children
  }
}

function OrderFormSheetContent({ cardId, onClose }: { cardId: string; onClose: () => void }) {
  const t = useTranslations('orderForm')
  // The sheet is its own form (`cardId`, the shared popup id) — rows land here when the
  // adding widget's link group has no Order Form (see computeOrderFormTarget). Only a forced
  // open (useOrderFormPopup's `force`, e.g. the preset tour) shows a docked form's ticket.
  const targetCardId = useAtomValue(orderFormPopupFormAtom) ?? cardId
  const { selectedInstruments, selectedOptions } = useAtomValue(
    orderFormSelectionAtomFamily(targetCardId)
  )
  const [expanded, setExpanded] = useAtom(orderFormPopupExpandedAtom)
  const store = useStore()

  // Single owner of the collapsible config for this popup instance, shared with the left
  // form's (hidden while expanded) CollapsibleAreaInOrderForm and the new right-column
  // OrderFormDetailPanel via context — calling this hook again in either child would race
  // two local states writing back to the same atom. See order-form-collapsible-context.ts.
  const { config: collapsibleConfig, updateConfig: updateCollapsibleConfig } =
    useOrderFormCollapsibleConfig(targetCardId)
  const collapsibleContextValue = useMemo(
    () => ({ config: collapsibleConfig, updateConfig: updateCollapsibleConfig }),
    [collapsibleConfig, updateCollapsibleConfig]
  )
  // No second column on a phone: the sheet already covers the screen, so the Order Trigger
  // stays in the form's own collapsible. The persisted `expanded` is left alone for desktop.
  const isMobileLayout = useIsMobileLayout()
  const showDetailPanel = expanded && !isMobileLayout

  // The popup is not a layout widget, so server-side widget-config restore never runs for
  // it — re-seed the order-type tab and amounts from the local popup config mirror instead.
  useEffect(() => {
    if (seededPopupCards.has(targetCardId)) return
    seededPopupCards.add(targetCardId)

    const saved = store.get(orderFormPopupConfigAtom)
    if (!saved || saved.cardId !== cardId) return

    if (
      !store.get(orderFormSummaryTabPickedAtomFamily(targetCardId)) &&
      saved.orderType &&
      (Object.values(OrderFormSummaryTab) as string[]).includes(saved.orderType)
    ) {
      store.set(orderFormSummaryTabAtomFamily(targetCardId), saved.orderType as OrderFormSummaryTab)
    }

    if (store.get(orderFormServerValuesAtomFamily(targetCardId)) === null) {
      const values: OrderFormServerValues = {}
      if (saved.itemAmounts) {
        try {
          const parsed = JSON.parse(saved.itemAmounts) as Record<string, string>
          if (parsed && typeof parsed === 'object') values.itemAmounts = parsed
        } catch {
          // corrupted snapshot — skip amounts
        }
      }
      if (Object.keys(values).length > 0) {
        store.set(orderFormServerValuesAtomFamily(targetCardId), values)
      }
    }

    // Rows only fill an empty ticket: the selection atom outlives the sheet, so a reopen
    // within the session must keep what's there.
    const rows = parseOrderRows(saved[ORDER_ROWS_CONFIG_KEY])
    const current = store.get(orderFormSelectionAtomFamily(targetCardId))
    if (rows && current.selectedOptions.length + current.selectedInstruments.length === 0) {
      store.set(orderFormSelectionAtomFamily(targetCardId), rows)
    }
  }, [cardId, targetCardId, store])

  const containerRef = useRef<HTMLDivElement>(null)
  const initialSelectionRef = useRef<{ cardId: string; hadSelection: boolean } | null>(null)
  const hasSelection = selectedOptions.length > 0 || selectedInstruments.length > 0

  if (initialSelectionRef.current?.cardId !== targetCardId) {
    initialSelectionRef.current = { cardId: targetCardId, hadSelection: hasSelection }
  }

  const headerLabel = (() => {
    if (selectedOptions.length > 0) {
      const first = selectedOptions[0].instrumentName
      const extra = selectedOptions.length + selectedInstruments.length - 1
      return extra > 0 ? `${first} +${extra}` : first
    }
    if (selectedInstruments.length > 0) {
      const first = selectedInstruments[0].symbol
      const extra = selectedInstruments.length - 1
      return extra > 0 ? `${first} +${extra}` : first
    }
    return ''
  })()

  useEffect(() => {
    if (!initialSelectionRef.current?.hadSelection) return
    if (hasSelection) return

    onClose()
  }, [hasSelection, onClose])

  const panelSizeToggle = isMobileLayout ? null : (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setExpanded((value) => !value)}
      title={expanded ? t('collapsePanel') : t('expandPanel')}
      aria-label={expanded ? t('collapsePanel') : t('expandPanel')}
      aria-pressed={expanded}
      className="h-9 w-7 cursor-pointer text-white/80 hover:bg-white/10 hover:text-white"
    >
      {expanded ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
    </Button>
  )

  return (
    <OrderFormProvider cardId={targetCardId}>
      <div className="flex h-9 shrink-0 items-center justify-between border-b border-white/10 select-none">
        <div className="flex min-w-0 flex-1 items-center gap-1 pl-1">
          <div className="shrink-0">
            <SavePresetButton />
          </div>
          <span className="truncate pl-1 text-sm font-medium text-white/80">{headerLabel}</span>
        </div>

        <div className="ml-2 flex shrink-0 items-center gap-1 pr-1">
          {panelSizeToggle}

          <Button
            aria-label={t('close')}
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-9 w-7 cursor-pointer text-white/80 hover:bg-white/10 hover:text-white"
          >
            <XIcon className="size-4" />
          </Button>
        </div>
      </div>

      <OrderFormCollapsibleContext.Provider value={collapsibleContextValue}>
        <ChatContainerContext.Provider value={{ containerRef, ownerDocument: document }}>
          <PopupOrderContext.Provider value={{ onOrderExecuted: noop }}>
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <div ref={containerRef} className="min-w-0 flex-1 overflow-hidden">
                <OrderFormSummary
                  cardId={targetCardId}
                  compact
                  shortcutOwnerCardId={cardId}
                  skipProvider
                  hideCollapsibleArea={showDetailPanel}
                />
              </div>
              {showDetailPanel && (
                <OverlayScrollY
                  className="min-w-0 flex-1 border-l border-white/10"
                  scrollerClassName="h-full"
                >
                  <OrderFormDetailPanel />
                </OverlayScrollY>
              )}
            </div>
          </PopupOrderContext.Provider>
        </ChatContainerContext.Provider>
      </OrderFormCollapsibleContext.Provider>
    </OrderFormProvider>
  )
}

export function OrderFormSheet() {
  const t = useTranslations('orderForm')
  const [cardId, setCardId] = useAtom(orderFormPopupCardIdAtom)
  const [, setAnchorRect] = useAtom(orderFormPopupAnchorAtom)
  const expanded = useAtomValue(orderFormPopupExpandedAtom)
  const isMobileLayout = useIsMobileLayout()
  // Mirrors OrderFormSheetContent's showDetailPanel: never two columns on a phone.
  const sheetWidth =
    expanded && !isMobileLayout ? ORDER_FORM_SHEET_EXPANDED_WIDTH : ORDER_FORM_SHEET_WIDTH

  // `inset-y-0` pins the panel to the layout viewport, which iOS Safari (and some Android
  // configurations) never resizes for the on-screen keyboard — only visualViewport shrinks.
  // On mobile, track it explicitly so the panel (and the inputs at its bottom) actually rise
  // above the keyboard instead of being hidden behind it.
  // Keyboard down, the classes' top-0 / bottom-[var(--status-bar-height,2.5rem)] apply — stopping above
  // StatusBar — and the style is undefined (see visualViewportFixedStyle).
  // Any touch device, whatever its width: an iPad's keyboard covers the panel just the same.
  const isMobileDevice = useIsMobileDevice()
  // Phone: the sheet covers the screen but stays non-modal (no overlay), so a swipe that ran
  // off the form's own scroller scrolled the dashboard behind it — and Safari's toolbars with
  // it, dragging the sheet along. Lock the page while it's open; the form still scrolls.
  useLockPageScroll(isMobileLayout && !!cardId, `[${ORDER_FORM_SHEET_ATTR}]`)
  const visualViewportRect = useVisualViewportRect()
  const mobileViewportStyle = isMobileDevice
    ? visualViewportFixedStyle(visualViewportRect)
    : undefined

  // Stable across re-renders — OrderFormSheetContent's auto-close effect depends on
  // this, and a fresh function identity every render (an inline arrow here previously)
  // reran that effect on every unrelated re-render of this component instead of only
  // when the selection actually emptied out.
  const handleClose = useCallback(() => {
    setCardId(null)
    setAnchorRect(null)
  }, [setCardId, setAnchorRect])

  return (
    <SheetPrimitive.Root open={!!cardId} modal={false}>
      <SheetPortal>
        {/* Content owns the visual shell so background/blur are present from the first
            mounted frame. The child only controls the current panel width. */}
        <SheetPrimitive.Content
          className="sheet-surface data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:slide-in-from-right fixed top-0 right-0 bottom-[var(--status-bar-height,2.5rem)] z-50 outline-none data-[state=closed]:duration-150 data-[state=closed]:ease-linear data-[state=open]:duration-150 data-[state=open]:ease-linear"
          style={mobileViewportStyle}
          {...{ [ORDER_FORM_SHEET_ATTR]: '' }}
          onEscapeKeyDown={(event) => {
            // Root has no onOpenChange (open is driven directly by cardId), so Radix's
            // default escape behavior wouldn't close anything — close explicitly instead.
            event.preventDefault()
            handleClose()
          }}
          onFocusOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <SheetPrimitive.Title className="sr-only">{t('orderForm')}</SheetPrimitive.Title>
          <div className="flex h-full max-w-dvw flex-col" style={{ width: sheetWidth }}>
            {cardId && (
              <OrderFormSheetErrorBoundary onClose={handleClose}>
                <OrderFormSheetContent cardId={cardId} onClose={handleClose} />
              </OrderFormSheetErrorBoundary>
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </SheetPrimitive.Root>
  )
}
